import React, { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { Section } from "@/components/Section";
import { IUserContext, UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IAssignment } from "@/data/assignments";
import { Flex, Tab, TabList, TabPanel, TabPanels, Tabs } from "@/eworkitComponents";
import { ContactPersonCard } from "@/views/components/Contact/ContactPersonCard";
import { WorkOrderHeader, WorkOrderSupplier } from "@/views/components/WorkOrder";

import { AssignmentDigest } from "./AssignmentDigest";
import { AssignmentRelated } from "./AssignmentRelated";
import { AssignmentWorkOrders } from "./AssignmentWorkOrders";
import { ContactPersonModalButton } from "./ContactPersonModalButton";

interface IAssignmentDetailsProps {
  assignment: IAssignment;
  update?: () => void;
}

export const AssignmentDetails: FC<IAssignmentDetailsProps> = ({ assignment, update }) => {
  const { t } = useTranslation("session");
  const User: IUserContext = useContext(UserContext);
  const hasCompanyAdminPrivileges = User.hasCompanyAdminPrivileges();
  const isAssignmentContactPerson = User.contextConsultantProfile?.id === assignment.assignmentConsultantProfile.id;
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const canEditContactPerson = hasCompanyAdminPrivileges || isAssignmentContactPerson;

  return (
    <Flex width={12} direction="column" innerMargin="md" margin={[false, false, "md", false]}>
      <Tabs>
        <WorkOrderHeader resource={assignment} update={update} hideActionButtons translationNamespace="ASSIGNMENTS">
          <TabList size="lg" innerMargin="xs" padding={["sm", false, false, false]}>
            <Tab>
              <Flex margin={[false, "md"]}>{t("ASSIGNMENTS.DETAILS.DETAILS")}</Flex>
            </Tab>
            <Tab>
              <Flex margin={[false, "md"]}>{t("ASSIGNMENTS.DETAILS.RELATED")}</Flex>
            </Tab>
          </TabList>
        </WorkOrderHeader>

        <TabPanels>
          <TabPanel padding={["md", false, false, false]}>
            <Section
              width={12}
              direction="column"
              shadow={isSmallScreen ? "none" : "default"}
              borderRadius={!isSmallScreen}
              padding={["lg"]}
              margin={[false, false, "md", false]}
              innerMargin="lg">
              <WorkOrderSupplier workOrder={assignment} />

              <AssignmentDigest assignment={assignment} />

              <AssignmentWorkOrders assignment={assignment} />
            </Section>

            {!assignment.otherSupplier && (
              <Section
                width={12}
                flex
                direction="row"
                innerMargin="sm"
                align="flex-start"
                justify="space-between"
                shadow={isSmallScreen ? "none" : "default"}
                borderRadius={!isSmallScreen}
                padding={["lg"]}>
                <ContactPersonCard consultantProfile={assignment.assignmentConsultantProfile} />

                {canEditContactPerson && (
                  <ContactPersonModalButton
                    variant="secondary"
                    icon="edit"
                    openableProps={{
                      assignmentId: assignment.id,
                      consultantProfile: assignment.assignmentConsultantProfile,
                      onSave: update,
                    }}
                  />
                )}
              </Section>
            )}
          </TabPanel>

          <TabPanel padding={["md", false, false, false]}>
            <Section
              width={12}
              direction="column"
              shadow={isSmallScreen ? "none" : "default"}
              borderRadius={!isSmallScreen}
              padding={["lg"]}
              innerMargin="lg">
              <AssignmentRelated assignment={assignment} />
            </Section>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
};
